import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiMusic,
  FiRadio,
  FiHeadphones,
  FiVolume2,
  FiMic,
  FiSpeaker,
  FiDisc,
  FiPlay,
  FiHeart,
  FiStar
} from 'react-icons/fi';
import Icon from '../Icon/Icon';

const FloatingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
  /* Promote to its own compositing layer for GPU acceleration */
  will-change: transform;
  contain: layout style paint;
`;

const FloatingIcon = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => prop !== 'iconColor',
}) <{ iconColor?: 'primary' | 'accent' }>`
  position: absolute;
  color: ${({ theme, iconColor }) =>
    iconColor === 'primary' ? theme.colors.primary : theme.colors.accent};
  opacity: 0.25;
  font-size: 2rem;
  pointer-events: none;
  /* Use transform + opacity only for GPU-accelerated animation */
  will-change: transform, opacity;
`;

interface FloatingIconData {
  id: number;
  icon: any;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  duration: number;
  color: 'primary' | 'accent';
  size: number;
  rotation: number;
}

const musicIcons = [
  FiMusic, FiRadio, FiHeadphones, FiVolume2, FiMic,
  FiSpeaker, FiDisc, FiPlay, FiHeart, FiStar
];

let iconIdCounter = 0;

const FloatingIcons: React.FC = () => {
  const [icons, setIcons] = useState<FloatingIconData[]>([]);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const createIcon = useCallback((): FloatingIconData => {
    const randomIcon = musicIcons[Math.floor(Math.random() * musicIcons.length)];
    const horizontal = Math.random() > 0.5;

    let startX: number, startY: number, endX: number, endY: number;

    if (horizontal) {
      const goRight = Math.random() > 0.5;
      startX = goRight ? -8 : 108;
      endX = goRight ? 108 : -8;
      startY = 15 + Math.random() * 70;
      endY = startY + (Math.random() - 0.5) * 20;
    } else {
      const goDown = Math.random() > 0.5;
      startY = goDown ? -8 : 108;
      endY = goDown ? 108 : -8;
      startX = 15 + Math.random() * 70;
      endX = startX + (Math.random() - 0.5) * 20;
    }

    return {
      id: ++iconIdCounter,
      icon: randomIcon,
      startX, startY,
      endX, endY,
      duration: 12 + Math.random() * 6, // 12-18 seconds for smoother travel
      color: Math.random() > 0.5 ? 'primary' : 'accent',
      size: 24 + Math.random() * 12,
      rotation: (Math.random() - 0.5) * 120, // -60 to 60 degrees
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Spawn 4 icons initially (reduced from 6)
    const initial: FloatingIconData[] = [];
    for (let i = 0; i < 4; i++) {
      initial.push(createIcon());
    }
    setIcons(initial);

    // Stagger-spawn a new icon every 3-4 seconds, remove old ones
    const spawnIcon = () => {
      if (!isMountedRef.current) return;

      setIcons(prev => {
        // Keep max 4 icons alive at once
        const trimmed = prev.length >= 4 ? prev.slice(1) : prev;
        return [...trimmed, createIcon()];
      });

      // Schedule next spawn
      intervalRef.current = setTimeout(spawnIcon, 3000 + Math.random() * 1500);
    };

    intervalRef.current = setTimeout(spawnIcon, 3000);

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [createIcon]);

  return (
    <FloatingContainer>
      <AnimatePresence>
        {icons.map((iconData) => (
          <FloatingIcon
            key={iconData.id}
            iconColor={iconData.color}
            initial={{
              x: `${iconData.startX}vw`,
              y: `${iconData.startY}vh`,
              opacity: 0,
              rotate: 0,
            }}
            animate={{
              x: `${iconData.endX}vw`,
              y: `${iconData.endY}vh`,
              opacity: [0, 0.3, 0.25, 0.25, 0],
              rotate: iconData.rotation,
            }}
            exit={{ opacity: 0 }}
            transition={{
              // Single linear tween for x/y — much cheaper than multi-keyframe
              duration: iconData.duration,
              ease: 'linear',
              opacity: {
                duration: iconData.duration,
                times: [0, 0.1, 0.3, 0.85, 1],
                ease: 'easeInOut',
              },
              rotate: {
                duration: iconData.duration,
                ease: 'linear',
              },
            }}
            // Auto-remove when animation finishes
            onAnimationComplete={() => {
              setIcons(prev => prev.filter(ic => ic.id !== iconData.id));
            }}
          >
            <Icon icon={iconData.icon} size={iconData.size} />
          </FloatingIcon>
        ))}
      </AnimatePresence>
    </FloatingContainer>
  );
};

export default FloatingIcons;
